import React, { FC } from 'react'

import Tooltip from 'uiKit/Tooltip'
import { SparklesIcon } from 'uiKit/icons/SparklesIcon'
import { AuditStatus } from '../../api/carePlanAuditApi'
import { CARE_PLAN_PACKAGE_DISABLED_REASON, PENDING_CARE_PLAN_REASON } from '../../helpers/carePlanAuditGate'

import { AuditButton, AuditCell } from './CarePlanAudit.styles'

export type CarePlanAuditState = {
  status?: AuditStatus
  /** Numeric audit version from the API (replaces the old job id). */
  version?: number
  /** PROCESSING came from the status endpoint (post-upload processing, or a run not started here). */
  serverBusy?: boolean
}

interface Props {
  patientId: number
  /** How many care plans this service user has — nothing to audit at zero. */
  carePlanCount: number
  audit?: CarePlanAuditState
  /**
   * Opens the audit screen for this service user — and only that.
   *
   * This cell used to start a job on click, so an allowance was spent before
   * anybody had seen what the audit covers or had a chance to change their mind.
   * Running one is now a decision made on the audit screen itself, against a
   * card that says what it checks. The same click also serves a job already
   * running and a finished report, so there is one way in whatever the state.
   */
  onOpen: (patientId: number) => void
  /**
   * Set when the package has audits off, a care plan is training, or the monthly
   * allowance is spent; the button stays visible but inert unless a report exists.
   */
  blockedReason?: string
}

const OPEN_REPORT_TIP = 'Click here to run and download a care plan audit.'

const IN_PROGRESS: AuditStatus[] = ['PENDING', 'PROCESSING']

/**
 * The care plan audit control for one service user.
 *
 * The unit is the person, not the document. An audit reviews the care somebody
 * receives across every care plan held for them, and the monthly allowance
 * counts it that way — auditing a service user with four documents costs one,
 * not four. A per-document button implied the opposite and would have burned an
 * allowance four times over for a single person.
 */
export const CarePlanAudit: FC<Props> = ({ patientId, carePlanCount, audit, onOpen, blockedReason }) => {
  const tipId = `cp-audit-${patientId}`

  if (carePlanCount === 0) {
    return (
      <AuditCell>
        <span data-for={tipId} data-tip>
          <AuditButton disabled>
            <SparklesIcon />
            Care plan audit
          </AuditButton>
        </span>
        <Tooltip id={tipId}>Upload a care plan first to run an audit</Tooltip>
      </AuditCell>
    )
  }

  if (audit?.status === 'READY' && audit.version != null) {
    // Same span tip target as idle/zero-plan — react-tooltip@3 is unreliable when
    // data-for/data-tip sit on the button itself after hydrate remounts READY.
    // Opening an existing report stays enabled even if blockedReason is set;
    // new runs are gated on the audit screen / handleGenerateAudit.
    return (
      <AuditCell>
        <span data-for={tipId} data-tip>
          <AuditButton onClick={() => onOpen(patientId)}>
            <SparklesIcon />
            Care plan audit
          </AuditButton>
        </span>
        <Tooltip id={tipId}>{OPEN_REPORT_TIP}</Tooltip>
      </AuditCell>
    )
  }

  const inProgress = Boolean(audit?.status && IN_PROGRESS.includes(audit.status))
  // A block reason (package off, training, allowance spent) or server-side work
  // this page did not start makes this inert — the audit screen could only show
  // Run as blocked. A run started from the audit screen stays clickable so
  // closing it does not strand the operator with no way back to that job.
  // An existing report can always be opened; blocks only stop a new run.
  const hasReport = audit?.version != null
  const disabled = (Boolean(blockedReason) || Boolean(audit?.serverBusy)) && !hasReport

  // Processing / a run in progress only blocks a new audit. With an earlier
  // report the button opens it, so say that instead.
  const processingOrRunning = inProgress || blockedReason === PENDING_CARE_PLAN_REASON

  // Priority: deactivated → open existing report (while processing / running)
  // → training / running → limit exceeded.
  const tooltip = blockedReason === CARE_PLAN_PACKAGE_DISABLED_REASON
    ? blockedReason
    : processingOrRunning && hasReport
      ? OPEN_REPORT_TIP
      : inProgress
        ? PENDING_CARE_PLAN_REASON
        : blockedReason ||
          'Review the care plan for this service user against the fundamental standards'

  return (
    <AuditCell>
      <span data-for={tipId} data-tip>
        <AuditButton disabled={disabled} onClick={() => !disabled && onOpen(patientId)}>
          <SparklesIcon />
          Care plan audit
        </AuditButton>
      </span>
      <Tooltip id={tipId}>{tooltip}</Tooltip>
    </AuditCell>
  )
}

export default CarePlanAudit
